import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";

const SUBJECTS = ["General Enquiry", "Custom Rug Commission", "Interior Designer / Trade", "Hospitality / Bulk", "Press & Editorial", "Care & Restoration"];

export default function V2Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Please share your name, email and message");
    setLoading(true);
    try {
      await api.post("/contact", f);
      setSent(true);
      toast.success("We'll be in touch shortly");
      setF({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="v2-contact-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-16">
        <div className="overline-v2">Say hello</div>
        <h1 className="mt-5 font-serif-warm text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          A conversation,<br/><em>not a form.</em>
        </h1>
        <p className="mt-8 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-2xl">
          Whether it's a commission, a partnership, or just a question about knot density — we love hearing from considered homes.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          {[
            { icon: MapPin, t: "Studio", v: <>17, Lavelle Lane<br/>Bengaluru 560 001</> },
            { icon: Mail, t: "Email", v: <a href="mailto:info@iryaahomes.in" className="v2-link">info@iryaahomes.in</a> },
            { icon: Phone, t: "Phone", v: <a href="tel:+918971188977" className="v2-link">+91 89711 88977</a> },
          ].map((c) => (
            <div key={c.t} className="border-t border-[#1a1a1a] pt-5">
              <div className="flex items-center gap-2 text-[#b85742]"><c.icon size={14} /><span className="overline-v2 text-[10px]">{c.t}</span></div>
              <div className="mt-2 font-serif-warm text-2xl text-[#1a1a1a] leading-tight">{c.v}</div>
            </div>
          ))}
          <div className="border-t border-[#1a1a1a] pt-5">
            <div className="overline-v2 text-[10px]">Fastest reply</div>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="v2-contact-whatsapp" className="mt-3 inline-flex v2-btn-primary">
              <MessageCircle size={13} /> WhatsApp us
            </a>
            <p className="mt-4 text-[13px] text-[#7a7466] font-light">Mon – Sat · 10.00 to 19.00 IST. Usually replies within an hour.</p>
          </div>
          <div className="border-t border-[#1a1a1a] pt-5">
            <div className="overline-v2 text-[10px]">Interior designers & hospitality</div>
            <p className="mt-3 text-[14px] text-[#4a4640] font-light leading-relaxed">Curated tradebooks, dedicated samples, and preferential pricing for design partners, hotels, restaurants and gifting programmes. Choose "Interior Designer / Trade" in the form.</p>
          </div>
        </div>

        <form onSubmit={submit} data-testid="v2-contact-form" className="lg:col-span-7 border border-[#e4ded2] bg-[#fbf9f5] p-8 md:p-10">
          {sent ? (
            <div className="text-center py-16">
              <div className="overline-v2">Thank you</div>
              <div className="mt-4 font-serif-warm text-4xl">Your note has landed on our desk.</div>
              <p className="mt-4 text-[15px] text-[#4a4640] font-light">We'll write back within one working day.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <L t="Your name"><input data-testid="v2-contact-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={I} /></L>
              <L t="Email"><input type="email" data-testid="v2-contact-email" value={f.email} onChange={(e) => set("email", e.target.value)} className={I} /></L>
              <L t="Phone (optional)"><input data-testid="v2-contact-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={I} /></L>
              <L t="Nature of enquiry"><select data-testid="v2-contact-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} className={I}>{SUBJECTS.map((s) => <option key={s}>{s}</option>)}</select></L>
              <L t="Your message" span2><textarea data-testid="v2-contact-message" rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} className={I} /></L>
              <div className="md:col-span-2 mt-4">
                <button type="submit" disabled={loading} data-testid="v2-contact-submit" className="v2-btn-primary w-full justify-center">
                  {loading ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : <>Send message</>}
                </button>
              </div>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
const I = "mt-2 w-full border-0 border-b border-[#c9c0ae] bg-transparent py-3 text-[15px] focus:border-[#1a1a1a] outline-none";
function L({ t, span2, children }) {
  return <label className={`block ${span2 ? "md:col-span-2" : ""}`}><span className="text-[11px] uppercase tracking-[0.22em] text-[#7a7466]">{t}</span>{children}</label>;
}
